/**
 * Layout calculation functions for PDF generation
 */

import jsPDF from 'jspdf';
import { ParsedLine } from '../leadsheetParser';

export interface PDFLayoutOptions {
  fontSize: number;
  chordFontSize: number;
  columns: number;
}

export interface LayoutAnalysis {
  adjustedFontSize: number;
  adjustedChordFontSize: number;
  sectionIndices: number[];
}

/**
 * Analyze sections and calculate optimal font size for 2-column layout
 */
export function analyzeSectionsAndFontSize(
  parsedLines: ParsedLine[],
  options: PDFLayoutOptions,
  doc: jsPDF,
  columnWidth: number
): LayoutAnalysis {
  // Find all section indices
  const sectionIndices = parsedLines
    .map((line, index) => (line.isSection ? index : -1))
    .filter(index => index !== -1);

  let adjustedFontSize = options.fontSize;
  let adjustedChordFontSize = options.chordFontSize;
  const minFontSize = 11;

  // If not 2 columns, no adjustment needed
  if (options.columns !== 2) {
    return { adjustedFontSize, adjustedChordFontSize, sectionIndices };
  }

  // Find the widest line in the entire document
  doc.setFont('courier', 'normal');
  let maxTextWidth = 0;

  for (const line of parsedLines) {
    if (!line.text) continue;
    
    doc.setFontSize(adjustedFontSize);
    const textWidth = doc.getTextWidth(line.text);
    maxTextWidth = Math.max(maxTextWidth, textWidth);
  }

  // Check if we need to reduce font size
  // Available width per column (accounting for spacing between columns)
  const availableWidth = columnWidth;

  // Reduce font size if necessary (proportionally reduce both text and chord fonts)
  while (maxTextWidth > availableWidth && adjustedFontSize > minFontSize) {
    const reductionRatio = 0.9; // Reduce by 10% each iteration
    adjustedFontSize = Math.max(minFontSize, adjustedFontSize * reductionRatio);
    adjustedChordFontSize = Math.max(minFontSize, adjustedChordFontSize * reductionRatio);

    // Recalculate max width with new font size
    doc.setFontSize(adjustedFontSize);
    maxTextWidth = 0;
    for (const line of parsedLines) {
      if (!line.text) continue;
      const textWidth = doc.getTextWidth(line.text);
      maxTextWidth = Math.max(maxTextWidth, textWidth);
    }
  }

  return { adjustedFontSize, adjustedChordFontSize, sectionIndices };
}

/**
 * Break a line into multiple lines if it exceeds column width
 * Chords follow their original positions
 */
export function breakLineIfNeeded(
  line: ParsedLine,
  columnWidth: number,
  fontSize: number,
  doc: jsPDF
): ParsedLine[] {
  if (!line.text || line.isSection) {
    return [line];
  }

  doc.setFont('courier', 'normal');
  doc.setFontSize(fontSize);
  
  const lineWidth = doc.getTextWidth(line.text);
  
  // If line fits, return as-is
  if (lineWidth <= columnWidth) {
    return [line];
  }

  // Break line into chunks that fit
  const result: ParsedLine[] = [];
  const words = line.text.split(' ');
  let currentChunk = '';
  let currentChunkStart = 0;

  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    const testChunk = currentChunk ? currentChunk + ' ' + word : word;
    const testWidth = doc.getTextWidth(testChunk);

    if (testWidth > columnWidth && currentChunk) {
      // Current chunk is full, save it
      const chunkEnd = currentChunkStart + currentChunk.length;
      const chunkStartCopy = currentChunkStart; // Capture for closure
      
      // Find chords that belong to this chunk
      const chunkedChords = line.chords.filter(
        chord => chord.position >= chunkStartCopy && chord.position < chunkEnd
      ).map(chord => ({
        chord: chord.chord,
        position: chord.position - chunkStartCopy
      }));

      result.push({
        text: currentChunk,
        chords: chunkedChords,
        isSection: false
      });

      // Start new chunk
      currentChunk = word;
      currentChunkStart = chunkEnd + 1; // +1 for space
    } else {
      currentChunk = testChunk;
    }
  }

  // Add remaining chunk
  if (currentChunk) {
    const chunkEnd = currentChunkStart + currentChunk.length;
    const chunkStartCopy = currentChunkStart; // Capture for closure
    const chunkedChords = line.chords.filter(
      chord => chord.position >= chunkStartCopy && chord.position <= chunkEnd
    ).map(chord => ({
      chord: chord.chord,
      position: chord.position - chunkStartCopy
    }));

    result.push({
      text: currentChunk,
      chords: chunkedChords,
      isSection: false
    });
  }

  return result.length > 0 ? result : [line];
}

